/**
 * Unified icon module. Wraps the lucide-react icon set under the names the app
 * uses, applying a slim default `strokeWidth` to match the Martini logo (see
 * `slim` below). Importing icons through this module (rather than `lucide-react`
 * directly) keeps every call site on a single, swappable icon surface with a
 * consistent stroke weight.
 *
 * No `'use client'` directive: the wrappers are plain forwardRef components with
 * no hooks or browser APIs, so server components can import icons without being
 * pulled into the client bundle.
 */

import { forwardRef } from 'react'
import {
  AlertCircle as LucideAlertCircle,
  AlertTriangle as LucideAlertTriangle,
  AlignCenter as LucideAlignCenter,
  AlignLeft as LucideAlignLeft,
  AlignRight as LucideAlignRight,
  ArrowDown as LucideArrowDown,
  ArrowDownRight as LucideArrowDownRight,
  ArrowLeft as LucideArrowLeft,
  ArrowLeftRight as LucideArrowLeftRight,
  ArrowLeftToLine as LucideArrowLeftToLine,
  ArrowRight as LucideArrowRight,
  ArrowUp as LucideArrowUp,
  ArrowUpDown as LucideArrowUpDown,
  ArrowUpRight as LucideArrowUpRight,
  AudioLines as LucideAudioLines,
  BarChart3 as LucideBarChart3,
  Bookmark as LucideBookmark,
  BookOpen as LucideBookOpen,
  Building2 as LucideBuilding2,
  Bell as LucideBell,
  Bold as LucideBold,
  Bot as LucideBot,
  Brain as LucideBrain,
  Box as LucideBox,
  BoxSelect as LucideBoxSelect,
  Braces as LucideBraces,
  Brush as LucideBrush,
  Calendar as LucideCalendar,
  Camera as LucideCamera,
  Check as LucideCheck,
  CheckCheck as LucideCheckCheck,
  Clapperboard as LucideClapperboard,
  Code as LucideCode,
  Compass as LucideCompass,
  CheckCircle as LucideCheckCircle,
  CheckCircle2 as LucideCheckCircle2,
  CircleAlert as LucideCircleAlert,
  ChevronDown as LucideChevronDown,
  ChevronLeft as LucideChevronLeft,
  ChevronRight as LucideChevronRight,
  ChevronUp as LucideChevronUp,
  ChevronsUpDown as LucideChevronsUpDown,
  ChevronsDownUp as LucideChevronsDownUp,
  ChevronsLeft as LucideChevronsLeft,
  ChevronsRight as LucideChevronsRight,
  Circle as LucideCircle,
  CircleSlash as LucideCircleSlash,
  Clipboard as LucideClipboard,
  ClipboardList as LucideClipboardList,
  Clock as LucideClock,
  Copy as LucideCopy,
  CopyPlus as LucideCopyPlus,
  CornerDownRight as LucideCornerDownRight,
  CornerDownLeft as LucideCornerDownLeft,
  CreditCard as LucideCreditCard,
  Crop as LucideCrop,
  Crosshair as LucideCrosshair,
  Database as LucideDatabase,
  Download as LucideDownload,
  Edit2 as LucideEdit2,
  Eraser as LucideEraser,
  ExternalLink as LucideExternalLink,
  Eye as LucideEye,
  EyeOff as LucideEyeOff,
  FastForward as LucideFastForward,
  File as LucideFile,
  FileAudio as LucideFileAudio,
  FileImage as LucideFileImage,
  FileJson as LucideFileJson,
  FileText as LucideFileText,
  FileVideo as LucideFileVideo,
  Flag as LucideFlag,
  Film as LucideFilm,
  FlipHorizontal2 as LucideFlipHorizontal2,
  FlipVertical2 as LucideFlipVertical2,
  Filter as LucideFilter,
  FlaskConical as LucideFlaskConical,
  FoldHorizontal as LucideFoldHorizontal,
  Folder as LucideFolder,
  FolderClosed as LucideFolderClosed,
  FolderOpen as LucideFolderOpen,
  FolderPlus as LucideFolderPlus,
  Fullscreen as LucideFullscreen,
  GalleryHorizontalEnd as LucideGalleryHorizontalEnd,
  GalleryVerticalEnd as LucideGalleryVerticalEnd,
  Gauge as LucideGauge,
  Gift as LucideGift,
  GitBranch as LucideGitBranch,
  GitFork as LucideGitFork,
  Globe as LucideGlobe,
  GripHorizontal as LucideGripHorizontal,
  GripVertical as LucideGripVertical,
  Group as LucideGroup,
  Hand as LucideHand,
  Hash as LucideHash,
  Highlighter as LucideHighlighter,
  Heart as LucideHeart,
  HelpCircle as LucideHelpCircle,
  Spline as LucideSpline,
  Home as LucideHome,
  Image as LucideImage,
  ImageIcon as LucideImageIcon,
  ImageOff as LucideImageOff,
  Images as LucideImages,
  Info as LucideInfo,
  Italic as LucideItalic,
  Key as LucideKey,
  KeyRound as LucideKeyRound,
  Keyboard as LucideKeyboard,
  Lasso as LucideLasso,
  Layers as LucideLayers,
  LayoutDashboard as LucideLayoutDashboard,
  LayoutGrid as LucideLayoutGrid,
  Library as LucideLibrary,
  LifeBuoy as LucideLifeBuoy,
  Link as LucideLink,
  ListMinus as LucideListMinus,
  Loader as LucideLoader,
  Loader2 as LucideLoader2,
  Lock as LucideLock,
  LogIn as LucideLogIn,
  LogOut as LucideLogOut,
  Magnet as LucideMagnet,
  Mail as LucideMail,
  Map as LucideMap,
  MapPin as LucideMapPin,
  Maximize as LucideMaximize,
  Maximize2 as LucideMaximize2,
  Menu as LucideMenu,
  Merge as LucideMerge,
  MessageCircle as LucideMessageCircle,
  MessageSquare as LucideMessageSquare,
  Mic as LucideMic,
  MicOff as LucideMicOff,
  Minimize as LucideMinimize,
  Minimize2 as LucideMinimize2,
  Minus as LucideMinus,
  Monitor as LucideMonitor,
  MoreHorizontal as LucideMoreHorizontal,
  MoreVertical as LucideMoreVertical,
  Mouse as LucideMouse,
  MousePointer2 as LucideMousePointer2,
  MousePointerClick as LucideMousePointerClick,
  Move as LucideMove,
  MoveHorizontal as LucideMoveHorizontal,
  Music as LucideMusic,
  PanelLeftClose as LucidePanelLeftClose,
  PanelLeftOpen as LucidePanelLeftOpen,
  PanelRightOpen as LucidePanelRightOpen,
  Paperclip as LucidePaperclip,
  Pause as LucidePause,
  PenLine as LucidePenLine,
  Pin as LucidePin,
  Pencil as LucidePencil,
  Play as LucidePlay,
  PlayCircle as LucidePlayCircle,
  Plus as LucidePlus,
  Receipt as LucideReceipt,
  Redo2 as LucideRedo2,
  RefreshCw as LucideRefreshCw,
  Repeat as LucideRepeat,
  Replace as LucideReplace,
  Rewind as LucideRewind,
  RotateCcw as LucideRotateCcw,
  Rows3 as LucideRows3,
  Save as LucideSave,
  ScanSearch as LucideScanSearch,
  Scissors as LucideScissors,
  Search as LucideSearch,
  Send as LucideSend,
  Settings as LucideSettings,
  Settings2 as LucideSettings2,
  Shapes as LucideShapes,
  Share2 as LucideShare2,
  SkipBack as LucideSkipBack,
  SkipForward as LucideSkipForward,
  Smartphone as LucideSmartphone,
  Sparkles as LucideSparkles,
  Square as LucideSquare,
  SquareDashedMousePointer as LucideSquareDashedMousePointer,
  SquarePen as LucideSquarePen,
  SquareScissors as LucideSquareScissors,
  SquareSplitHorizontal as LucideSquareSplitHorizontal,
  Star as LucideStar,
  Strikethrough as LucideStrikethrough,
  Sun as LucideSun,
  Tag as LucideTag,
  ThumbsDown as LucideThumbsDown,
  ThumbsUp as LucideThumbsUp,
  ToggleLeft as LucideToggleLeft,
  ToggleRight as LucideToggleRight,
  Tablet as LucideTablet,
  Trash2 as LucideTrash2,
  TrendingUp as LucideTrendingUp,
  TriangleAlert as LucideTriangleAlert,
  Type as LucideType,
  Undo2 as LucideUndo2,
  Ungroup as LucideUngroup,
  Unlink as LucideUnlink,
  Unlock as LucideUnlock,
  Upload as LucideUpload,
  User as LucideUser,
  UserCircle as LucideUserCircle,
  UserX as LucideUserX,
  Users as LucideUsers,
  Video as LucideVideo,
  Volume2 as LucideVolume2,
  VolumeOff as LucideVolumeOff,
  VolumeX as LucideVolumeX,
  Wand2 as LucideWand2,
  Waypoints as LucideWaypoints,
  Wifi as LucideWifi,
  WifiOff as LucideWifiOff,
  Workflow as LucideWorkflow,
  Wrench as LucideWrench,
  X as LucideX,
  XCircle as LucideXCircle,
  Zap as LucideZap,
  ZoomIn as LucideZoomIn,
  ZoomOut as LucideZoomOut,
  createLucideIcon,
} from 'lucide-react'
import type { LucideIcon, LucideProps } from 'lucide-react'

// lucide's upstream `pencil-sparkles` — added to the library after our pinned
// version, so the official path data is inlined here via createLucideIcon
// (identical runtime behavior to every other icon). Swap for the library
// export on the next lucide bump.
const LucidePencilSparkles = /* @__PURE__ */ createLucideIcon('PencilSparkles', [
  ['path', { d: 'M10 3H8', key: 'p1' }],
  ['path', { d: 'm15.007 5.008 3.987 3.986', key: 'p2' }],
  ['path', { d: 'M20 15v4', key: 'p3' }],
  [
    'path',
    {
      d: 'M21.174 6.813a2.82 2.82 0 0 0-3.986-3.987L3.842 16.175a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z',
      key: 'p4',
    },
  ],
  ['path', { d: 'M22 17h-4', key: 'p5' }],
  ['path', { d: 'M4 5v4', key: 'p6' }],
  ['path', { d: 'M6 7H2', key: 'p7' }],
  ['path', { d: 'M9 2v2', key: 'p8' }],
])

type IconProps = React.SVGProps<SVGSVGElement>
export type IconComponent = React.ComponentType<IconProps>

// Slim stroke to match the Martini logo. Lucide defaults to strokeWidth 2; we
// render every wrapped icon a touch thinner. Applied before the `{...props}`
// spread so any caller passing an explicit `strokeWidth` (e.g. 2.5 for tiny
// check marks) still wins. `slim` is a plain forwardRef component — no hooks or
// browser APIs — so this module stays server-safe (importable from RSC).
// Each export is marked /* @__PURE__ */ so bundlers can drop unused icons
// (the call only builds + names a local component; no real side effects).
const DEFAULT_STROKE_WIDTH = 1.5

function slim(Icon: LucideIcon): LucideIcon {
  const Wrapped = forwardRef<SVGSVGElement, LucideProps>(function Slim(props, ref) {
    return <Icon ref={ref} strokeWidth={DEFAULT_STROKE_WIDTH} {...props} />
  })
  Wrapped.displayName = `Slim(${Icon.displayName ?? 'Icon'})`
  return Wrapped as unknown as LucideIcon
}

export const AlertCircle = /* @__PURE__ */ slim(LucideAlertCircle)
export const AlertTriangle = /* @__PURE__ */ slim(LucideAlertTriangle)
export const AlignCenter = /* @__PURE__ */ slim(LucideAlignCenter)
export const AlignLeft = /* @__PURE__ */ slim(LucideAlignLeft)
export const AlignRight = /* @__PURE__ */ slim(LucideAlignRight)
export const ArrowDown = /* @__PURE__ */ slim(LucideArrowDown)
export const ArrowDownRight = /* @__PURE__ */ slim(LucideArrowDownRight)
export const ArrowLeft = /* @__PURE__ */ slim(LucideArrowLeft)
export const ArrowLeftRight = /* @__PURE__ */ slim(LucideArrowLeftRight)
export const ArrowLeftToLine = /* @__PURE__ */ slim(LucideArrowLeftToLine)
export const ArrowRight = /* @__PURE__ */ slim(LucideArrowRight)
export const ArrowUp = /* @__PURE__ */ slim(LucideArrowUp)
export const ArrowUpDown = /* @__PURE__ */ slim(LucideArrowUpDown)
export const ArrowUpRight = /* @__PURE__ */ slim(LucideArrowUpRight)
export const AudioLines = /* @__PURE__ */ slim(LucideAudioLines)
export const BarChart3 = /* @__PURE__ */ slim(LucideBarChart3)
export const Bell = /* @__PURE__ */ slim(LucideBell)
export const BookOpen = /* @__PURE__ */ slim(LucideBookOpen)
export const Bookmark = /* @__PURE__ */ slim(LucideBookmark)
export const Building2 = /* @__PURE__ */ slim(LucideBuilding2)
export const Bold = /* @__PURE__ */ slim(LucideBold)
export const Bot = /* @__PURE__ */ slim(LucideBot)
export const Brain = /* @__PURE__ */ slim(LucideBrain)
export const Box = /* @__PURE__ */ slim(LucideBox)
export const BoxSelect = /* @__PURE__ */ slim(LucideBoxSelect)
export const Braces = /* @__PURE__ */ slim(LucideBraces)
export const Brush = /* @__PURE__ */ slim(LucideBrush)
export const Calendar = /* @__PURE__ */ slim(LucideCalendar)
export const Camera = /* @__PURE__ */ slim(LucideCamera)
export const Check = /* @__PURE__ */ slim(LucideCheck)
export const CheckCheck = /* @__PURE__ */ slim(LucideCheckCheck)
export const Clapperboard = /* @__PURE__ */ slim(LucideClapperboard)
export const Code = /* @__PURE__ */ slim(LucideCode)
export const Compass = /* @__PURE__ */ slim(LucideCompass)
export const CheckCircle = /* @__PURE__ */ slim(LucideCheckCircle)
export const CheckCircle2 = /* @__PURE__ */ slim(LucideCheckCircle2)
export const CircleAlert = /* @__PURE__ */ slim(LucideCircleAlert)
export const ChevronDown = /* @__PURE__ */ slim(LucideChevronDown)
export const ChevronLeft = /* @__PURE__ */ slim(LucideChevronLeft)
export const ChevronRight = /* @__PURE__ */ slim(LucideChevronRight)
export const ChevronUp = /* @__PURE__ */ slim(LucideChevronUp)
export const ChevronsUpDown = /* @__PURE__ */ slim(LucideChevronsUpDown)
export const ChevronsDownUp = /* @__PURE__ */ slim(LucideChevronsDownUp)
export const ChevronsLeft = /* @__PURE__ */ slim(LucideChevronsLeft)
export const ChevronsRight = /* @__PURE__ */ slim(LucideChevronsRight)
export const Circle = /* @__PURE__ */ slim(LucideCircle)
export const CircleSlash = /* @__PURE__ */ slim(LucideCircleSlash)
export const Clipboard = /* @__PURE__ */ slim(LucideClipboard)
export const ClipboardList = /* @__PURE__ */ slim(LucideClipboardList)
export const Clock = /* @__PURE__ */ slim(LucideClock)
export const Copy = /* @__PURE__ */ slim(LucideCopy)
export const CopyPlus = /* @__PURE__ */ slim(LucideCopyPlus)
export const CornerDownRight = /* @__PURE__ */ slim(LucideCornerDownRight)
export const CornerDownLeft = /* @__PURE__ */ slim(LucideCornerDownLeft)
export const CreditCard = /* @__PURE__ */ slim(LucideCreditCard)
export const Crop = /* @__PURE__ */ slim(LucideCrop)
export const Crosshair = /* @__PURE__ */ slim(LucideCrosshair)
export const Database = /* @__PURE__ */ slim(LucideDatabase)
export const Download = /* @__PURE__ */ slim(LucideDownload)
export const Edit2 = /* @__PURE__ */ slim(LucideEdit2)
export const Eraser = /* @__PURE__ */ slim(LucideEraser)
export const ExternalLink = /* @__PURE__ */ slim(LucideExternalLink)
export const Eye = /* @__PURE__ */ slim(LucideEye)
export const EyeOff = /* @__PURE__ */ slim(LucideEyeOff)
export const FastForward = /* @__PURE__ */ slim(LucideFastForward)
export const File = /* @__PURE__ */ slim(LucideFile)
export const FileAudio = /* @__PURE__ */ slim(LucideFileAudio)
export const FileImage = /* @__PURE__ */ slim(LucideFileImage)
export const FileJson = /* @__PURE__ */ slim(LucideFileJson)
export const FileText = /* @__PURE__ */ slim(LucideFileText)
export const FileVideo = /* @__PURE__ */ slim(LucideFileVideo)
export const Flag = /* @__PURE__ */ slim(LucideFlag)
export const FlipHorizontal2 = /* @__PURE__ */ slim(LucideFlipHorizontal2)
export const FlipVertical2 = /* @__PURE__ */ slim(LucideFlipVertical2)
export const Film = /* @__PURE__ */ slim(LucideFilm)
export const Filter = /* @__PURE__ */ slim(LucideFilter)
export const FlaskConical = /* @__PURE__ */ slim(LucideFlaskConical)
export const FoldHorizontal = /* @__PURE__ */ slim(LucideFoldHorizontal)
export const Folder = /* @__PURE__ */ slim(LucideFolder)
export const FolderClosed = /* @__PURE__ */ slim(LucideFolderClosed)
export const FolderOpen = /* @__PURE__ */ slim(LucideFolderOpen)
export const FolderPlus = /* @__PURE__ */ slim(LucideFolderPlus)
export const Fullscreen = /* @__PURE__ */ slim(LucideFullscreen)
export const GalleryHorizontalEnd = /* @__PURE__ */ slim(LucideGalleryHorizontalEnd)
export const GalleryVerticalEnd = /* @__PURE__ */ slim(LucideGalleryVerticalEnd)
export const CollectionIcon = BookOpen
export const Gauge = /* @__PURE__ */ slim(LucideGauge)
export const Gift = /* @__PURE__ */ slim(LucideGift)
export const GitBranch = /* @__PURE__ */ slim(LucideGitBranch)
export const GitFork = /* @__PURE__ */ slim(LucideGitFork)
export const Globe = /* @__PURE__ */ slim(LucideGlobe)
export const GripHorizontal = /* @__PURE__ */ slim(LucideGripHorizontal)
export const GripVertical = /* @__PURE__ */ slim(LucideGripVertical)
export const Group = /* @__PURE__ */ slim(LucideGroup)
export const Hand = /* @__PURE__ */ slim(LucideHand)
export const Hash = /* @__PURE__ */ slim(LucideHash)
export const Highlighter = /* @__PURE__ */ slim(LucideHighlighter)
export const Heart = /* @__PURE__ */ slim(LucideHeart)
export const HelpCircle = /* @__PURE__ */ slim(LucideHelpCircle)
export const Spline = /* @__PURE__ */ slim(LucideSpline)
export const Home = /* @__PURE__ */ slim(LucideHome)
export const Image = /* @__PURE__ */ slim(LucideImage)
export const ImageIcon = /* @__PURE__ */ slim(LucideImageIcon)
export const ImageOff = /* @__PURE__ */ slim(LucideImageOff)
export const Images = /* @__PURE__ */ slim(LucideImages)
export const Info = /* @__PURE__ */ slim(LucideInfo)
export const Italic = /* @__PURE__ */ slim(LucideItalic)
export const Key = /* @__PURE__ */ slim(LucideKey)
export const KeyRound = /* @__PURE__ */ slim(LucideKeyRound)
export const Keyboard = /* @__PURE__ */ slim(LucideKeyboard)
export const Lasso = /* @__PURE__ */ slim(LucideLasso)
export const Layers = /* @__PURE__ */ slim(LucideLayers)
export const MapPin = /* @__PURE__ */ slim(LucideMapPin)
export const LibraryIcon = /* @__PURE__ */ slim(LucideLibrary)
export const StackIcon = /* @__PURE__ */ slim(LucideLayers)
export const LayoutDashboard = /* @__PURE__ */ slim(LucideLayoutDashboard)
export const LayoutGrid = /* @__PURE__ */ slim(LucideLayoutGrid)
export const LifeBuoy = /* @__PURE__ */ slim(LucideLifeBuoy)
export const Link = /* @__PURE__ */ slim(LucideLink)
export const ListMinus = /* @__PURE__ */ slim(LucideListMinus)
export const Loader = /* @__PURE__ */ slim(LucideLoader)
export const Loader2 = /* @__PURE__ */ slim(LucideLoader2)
export const Lock = /* @__PURE__ */ slim(LucideLock)
export const LogIn = /* @__PURE__ */ slim(LucideLogIn)
export const LogOut = /* @__PURE__ */ slim(LucideLogOut)
export const Magnet = /* @__PURE__ */ slim(LucideMagnet)
export const Mail = /* @__PURE__ */ slim(LucideMail)
export const Map = /* @__PURE__ */ slim(LucideMap)
export const Maximize = /* @__PURE__ */ slim(LucideMaximize)
export const Maximize2 = /* @__PURE__ */ slim(LucideMaximize2)
export const Menu = /* @__PURE__ */ slim(LucideMenu)
export const Merge = /* @__PURE__ */ slim(LucideMerge)
export const MessageSquare = /* @__PURE__ */ slim(LucideMessageSquare)
export const Mic = /* @__PURE__ */ slim(LucideMic)
export const MicOff = /* @__PURE__ */ slim(LucideMicOff)
export const Minimize = /* @__PURE__ */ slim(LucideMinimize)
export const Minimize2 = /* @__PURE__ */ slim(LucideMinimize2)
export const Minus = /* @__PURE__ */ slim(LucideMinus)
export const Monitor = /* @__PURE__ */ slim(LucideMonitor)
export const MoreHorizontal = /* @__PURE__ */ slim(LucideMoreHorizontal)
export const MoreVertical = /* @__PURE__ */ slim(LucideMoreVertical)
export const Mouse = /* @__PURE__ */ slim(LucideMouse)
export const MousePointer2 = /* @__PURE__ */ slim(LucideMousePointer2)
export const MousePointerClick = /* @__PURE__ */ slim(LucideMousePointerClick)
export const Move = /* @__PURE__ */ slim(LucideMove)
export const MoveHorizontal = /* @__PURE__ */ slim(LucideMoveHorizontal)
export const Music = /* @__PURE__ */ slim(LucideMusic)
export const PanelLeftClose = /* @__PURE__ */ slim(LucidePanelLeftClose)
export const PanelLeftOpen = /* @__PURE__ */ slim(LucidePanelLeftOpen)
export const PanelRightOpen = /* @__PURE__ */ slim(LucidePanelRightOpen)
export const Paperclip = /* @__PURE__ */ slim(LucidePaperclip)
export const Pause = /* @__PURE__ */ slim(LucidePause)
export const Pin = /* @__PURE__ */ slim(LucidePin)
export const PenLine = /* @__PURE__ */ slim(LucidePenLine)
export const Pencil = /* @__PURE__ */ slim(LucidePencil)
export const PencilSparkles = /* @__PURE__ */ slim(LucidePencilSparkles)
export const Play = /* @__PURE__ */ slim(LucidePlay)
export const PlayCircle = /* @__PURE__ */ slim(LucidePlayCircle)
export const Plus = /* @__PURE__ */ slim(LucidePlus)
export const Receipt = /* @__PURE__ */ slim(LucideReceipt)
export const Redo2 = /* @__PURE__ */ slim(LucideRedo2)
export const RefreshCw = /* @__PURE__ */ slim(LucideRefreshCw)
export const Repeat = /* @__PURE__ */ slim(LucideRepeat)
export const Replace = /* @__PURE__ */ slim(LucideReplace)
export const Rewind = /* @__PURE__ */ slim(LucideRewind)
export const RotateCcw = /* @__PURE__ */ slim(LucideRotateCcw)
export const Rows3 = /* @__PURE__ */ slim(LucideRows3)
export const Save = /* @__PURE__ */ slim(LucideSave)
export const ScanSearch = /* @__PURE__ */ slim(LucideScanSearch)
export const Scissors = /* @__PURE__ */ slim(LucideScissors)
export const Search = /* @__PURE__ */ slim(LucideSearch)
export const Settings = /* @__PURE__ */ slim(LucideSettings)
export const Settings2 = /* @__PURE__ */ slim(LucideSettings2)
export const Shapes = /* @__PURE__ */ slim(LucideShapes)
export const Share2 = /* @__PURE__ */ slim(LucideShare2)
export const SkipBack = /* @__PURE__ */ slim(LucideSkipBack)
export const SkipForward = /* @__PURE__ */ slim(LucideSkipForward)
export const Smartphone = /* @__PURE__ */ slim(LucideSmartphone)
export const Sparkles = /* @__PURE__ */ slim(LucideSparkles)
export const MessageCircle = /* @__PURE__ */ slim(LucideMessageCircle)
export const Send = /* @__PURE__ */ slim(LucideSend)
export const Square = /* @__PURE__ */ slim(LucideSquare)
export const SquareDashedMousePointer = /* @__PURE__ */ slim(LucideSquareDashedMousePointer)
export const SquarePen = /* @__PURE__ */ slim(LucideSquarePen)
export const SquareScissors = /* @__PURE__ */ slim(LucideSquareScissors)
export const SquareSplitHorizontal = /* @__PURE__ */ slim(LucideSquareSplitHorizontal)
export const Star = /* @__PURE__ */ slim(LucideStar)
export const Strikethrough = /* @__PURE__ */ slim(LucideStrikethrough)
export const Sun = /* @__PURE__ */ slim(LucideSun)
export const Tag = /* @__PURE__ */ slim(LucideTag)
export const ThumbsDown = /* @__PURE__ */ slim(LucideThumbsDown)
export const ThumbsUp = /* @__PURE__ */ slim(LucideThumbsUp)
export const ToggleLeft = /* @__PURE__ */ slim(LucideToggleLeft)
export const ToggleRight = /* @__PURE__ */ slim(LucideToggleRight)
export const Tablet = /* @__PURE__ */ slim(LucideTablet)
export const Trash2 = /* @__PURE__ */ slim(LucideTrash2)
export const TrendingUp = /* @__PURE__ */ slim(LucideTrendingUp)
export const TriangleAlert = /* @__PURE__ */ slim(LucideTriangleAlert)
export const Type = /* @__PURE__ */ slim(LucideType)
export const Undo2 = /* @__PURE__ */ slim(LucideUndo2)
export const Ungroup = /* @__PURE__ */ slim(LucideUngroup)
export const Unlink = /* @__PURE__ */ slim(LucideUnlink)
export const Unlock = /* @__PURE__ */ slim(LucideUnlock)
export const Upload = /* @__PURE__ */ slim(LucideUpload)
export const User = /* @__PURE__ */ slim(LucideUser)
export const UserCircle = /* @__PURE__ */ slim(LucideUserCircle)
export const UserX = /* @__PURE__ */ slim(LucideUserX)
export const Users = /* @__PURE__ */ slim(LucideUsers)
export const Video = /* @__PURE__ */ slim(LucideVideo)
export const Volume2 = /* @__PURE__ */ slim(LucideVolume2)
export const VolumeOff = /* @__PURE__ */ slim(LucideVolumeOff)
export const VolumeX = /* @__PURE__ */ slim(LucideVolumeX)
export const Wand2 = /* @__PURE__ */ slim(LucideWand2)
export const Waypoints = /* @__PURE__ */ slim(LucideWaypoints)
export const Wifi = /* @__PURE__ */ slim(LucideWifi)
export const WifiOff = /* @__PURE__ */ slim(LucideWifiOff)
export const Workflow = /* @__PURE__ */ slim(LucideWorkflow)
export const Wrench = /* @__PURE__ */ slim(LucideWrench)
export const X = /* @__PURE__ */ slim(LucideX)
export const XCircle = /* @__PURE__ */ slim(LucideXCircle)
export const Zap = /* @__PURE__ */ slim(LucideZap)
export const ZoomIn = /* @__PURE__ */ slim(LucideZoomIn)
export const ZoomOut = /* @__PURE__ */ slim(LucideZoomOut)
